import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-dark-bg py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-cream-gold hover:text-cream-gold-light transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </Link>

        <h1 className="text-4xl md:text-5xl font-bold text-cream-gold mb-4">
          Privacy Policy
        </h1>
        <p className="text-cream-gold-light/60 mb-8">Last updated: January 2025</p>

        <div className="space-y-8 text-cream-gold-light/80 leading-relaxed">
          <section>
            <h2 className="text-2xl font-bold text-cream-gold mb-4">1. Introduction</h2>
            <p>
              At Evox Media, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services. Please read this policy carefully.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-cream-gold mb-4">2. Information We Collect</h2>
            <h3 className="text-xl font-semibold text-cream-gold mb-3">Personal Information</h3>
            <p className="mb-4">We may collect personal information that you voluntarily provide to us when you:</p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li>Contact us via email or phone</li>
              <li>Fill out inquiry forms on our website</li>
              <li>Subscribe to our newsletter</li>
              <li>Engage our services</li>
            </ul>
            <p className="mb-4">This information may include:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Name and contact information</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Company name and business information</li>
              <li>Payment information (processed securely through third-party providers)</li>
            </ul>

            <h3 className="text-xl font-semibold text-cream-gold mb-3 mt-6">Automatically Collected Information</h3>
            <p className="mb-4">When you visit our website, we may automatically collect:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Pages visited and time spent</li>
              <li>Referring website</li>
              <li>Device information</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-cream-gold mb-4">3. How We Use Your Information</h2>
            <p className="mb-4">We use the information we collect to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide and manage our services</li>
              <li>Respond to your inquiries and requests</li>
              <li>Send administrative information and updates</li>
              <li>Improve our website and services</li>
              <li>Analyze usage patterns and trends</li>
              <li>Prevent fraudulent activity</li>
              <li>Comply with legal obligations</li>
              <li>Send marketing communications (with your consent)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-cream-gold mb-4">4. Information Sharing and Disclosure</h2>
            <p className="mb-4">We do not sell your personal information. We may share your information with:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Service Providers:</strong> Third parties who assist in operating our business (hosting, analytics, payment processing)</li>
              <li><strong>Business Partners:</strong> When necessary to provide services you've requested</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-cream-gold mb-4">5. Cookies and Tracking Technologies</h2>
            <p className="mb-4">
              We use cookies and similar tracking technologies to enhance your experience on our website. Cookies are small data files stored on your device. We use:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Essential Cookies:</strong> Required for website functionality</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
              <li><strong>Marketing Cookies:</strong> Track your browsing for targeted advertising</li>
            </ul>
            <p className="mt-4">
              You can control cookies through your browser settings, though disabling them may affect website functionality.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-cream-gold mb-4">6. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-cream-gold mb-4">7. Data Retention</h2>
            <p>
              We retain your personal information only for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required by law. When data is no longer needed, we will securely delete or anonymize it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-cream-gold mb-4">8. Your Privacy Rights</h2>
            <p className="mb-4">Depending on your location, you may have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to processing of your data</li>
              <li>Request data portability</li>
              <li>Withdraw consent for marketing communications</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, please contact us at business@evoxmedia.co
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-cream-gold mb-4">9. Third-Party Links</h2>
            <p>
              Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing any personal information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-cream-gold mb-4">10. Children's Privacy</h2>
            <p>
              Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If we become aware that we have collected data from a child, we will take steps to delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-cream-gold mb-4">11. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. By using our services, you consent to such transfers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-cream-gold mb-4">12. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website with an updated "Last Updated" date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-cream-gold mb-4">13. Contact Us</h2>
            <p>
              If you have questions or concerns about this Privacy Policy or our data practices, please contact us at:
            </p>
            <p className="mt-2">
              Email: <a href="mailto:business@evoxmedia.co" className="text-cream-gold hover:text-cream-gold-light">business@evoxmedia.co</a><br />
              Phone: <a href="tel:+919840084468" className="text-cream-gold hover:text-cream-gold-light">+91-9840084468</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;